import { inject } from '@angular/core';
import { Router, type CanActivateFn } from '@angular/router';
import { OrganizationService } from '../services/organization.service';
import { AuthService } from '../services/auth.service';
import { MsalBroadcastService } from '@azure/msal-angular';
import { InteractionStatus } from '@azure/msal-browser';
import { map, catchError, of, filter, take, switchMap } from 'rxjs';

export const hasOrganizationGuard: CanActivateFn = () => {
  const organizationService = inject(OrganizationService);
  const authService = inject(AuthService);
  const router = inject(Router);
  const msalBroadcastService = inject(MsalBroadcastService);

  // Wait for MSAL to finish initialization before checking organization
  return msalBroadcastService.inProgress$.pipe(
    filter((status: InteractionStatus) => status === InteractionStatus.None),
    take(1),
    switchMap(() => {
      const user = authService.getUser();
      if (!user?.localAccountId) {
        router.navigate(['/login']);
        return of(false);
      }

      return organizationService
        .checkUserOrganization(user.localAccountId)
        .pipe(
          map((response) => {
            if (response?.data?.organizationId) {
              // Check organization status
              const orgStatus = response.data.status;

              if (orgStatus && orgStatus !== 'Active') {
                // Organization is Inactive or Suspended - block access
                router.navigate(['/account-inactive']);
                return false;
              }

              // User has an active organization - allow access
              return true;
            }
            // User doesn't have organization - redirect to signup
            console.log('User needs to complete organization setup');
            router.navigate(['/onboarding/signup-process']);
            return false;
          }),
          catchError((error) => {
            console.error('Error checking organization status:', error);

            // Distinguish between different error types
            if (error.status === 404) {
              // 404 means user definitely has no organization
              console.log('User has no organization (404)');
              router.navigate(['/onboarding/signup-process']);
            } else {
              // Any other error (500, network, etc.) - redirect to error page
              console.error('API error or network issue:', error);
              router.navigate(['/error'], {
                queryParams: { type: 'organization-check-failed' },
              });
            }
            return of(false);
          }),
        );
    }),
  );
};
